<template>
  <form @submit.prevent="register" class="form">
    <ui-input
        :type="'text'"
        :placeholder="t('fields.firstName')"
        :rules="[required]"
        v-model="firstName"
    />
    <ui-input
        :type="'text'"
        :placeholder="t('fields.lastName')"
        :rules="[required]"
        v-model="lastName"
    />
    <ui-input
        :type="'email'"
        :placeholder="t('fields.email')"
        :rules="[isEmail]"
        v-model="email"
    />
    <ui-input
        :type="'password'"
        :placeholder="t('fields.password')"
        :rules="[isPasswordValid]"
        v-model="password"
    />
    <ui-input
        :type="'password'"
        :placeholder="t('fields.confirmPassword')"
        :rules="[compareStrings]"
        v-model="confirmPassword"
    />
    <p class="form__text">{{ t('auth.register.text') }} <router-link :to="translations.i18nRoute({ name: 'login' })">{{ t('buttons.signIn') }}</router-link></p>
    <ui-checkbox
        v-model="isAgree"
        :id="1"
    >
      <i18n-t tag="p" scope="global" keypath="checkboxes.accept">
        <template #terms>
          <router-link
              class="form__link"
              :to="translations.i18nRoute({ name: 'terms' })"
          >
            {{ t('docs.terms.title') }}
          </router-link>
        </template>
        <template #policy>
          <router-link
              class="form__link"
              :to="translations.i18nRoute({ name: 'policy' })"
          >
            {{ t('docs.policy.title') }}
          </router-link>
        </template>
      </i18n-t>
    </ui-checkbox>
    <ui-button
        :type="'submit'"
        class="form__button"
        :isDisabled="!isFormValid"
        :isLoading="isLoading"
    >
      {{ t('buttons.signUp') }}
      <svg xmlns="http://www.w3.org/2000/svg" width="27" height="28" viewBox="0 0 27 28" fill="none">
        <path d="M9.5625 6.125L17.4375 14L9.5625 21.875" stroke="#3b3b3b" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </ui-button>
  </form>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import {computed, h, ref} from "vue";
import {isEmail, isPasswordValid, required} from "@/core/rules/textFieldRules.js";
import UiInput from "@/components/ui/ui-input.vue";
import translations from "@/core/helpers/translations.js";
import UiCheckbox from "@/components/ui/ui-checkbox.vue";
import UiButton from "@/components/ui/ui-button.vue";
import {ElNotification} from "element-plus";
import {Auth} from "@/core/api/auth.js";

const {t} = useI18n()

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const isAgree = ref(false)

const isLoading = ref(false)
const mailClient = ref(null)

const compareStrings = (v) => {
  if (v === password.value) return true
  return t('errors.compare')
}

const isFormValid = computed(() => {
  return (
      required(firstName.value) === true &&
      required(lastName.value) === true &&
      isEmail(email.value) === true &&
      isPasswordValid(password.value) === true &&
      compareStrings(confirmPassword.value) === true &&
      isAgree.value === true
  )
})

const register = async () => {
  isLoading.value = true

  const res = await Auth.register({
    firstName: firstName.value,
    lastName: lastName.value,
    email: email.value,
    password: password.value,
    passwordConfirm: confirmPassword.value,
  })

  if (!res.errors) {
    const domain = email.value.split('@')[1]
    const mailClients = {
      'gmail.com': 'https://mail.google.com/',
      'outlook.com': 'https://outlook.live.com/',
      'icloud.com': 'https://www.icloud.com/',
      'yahoo.com': 'https://mail.yahoo.com/'
    }

    Object.entries(mailClients).forEach((el) => {
      if (domain === el[0]) mailClient.value = el[1]
    })

    ElNotification({
      message: h('div', [
        h('p', t('popup.successRegister')),
        mailClient.value ? h(
            'button',
            {
              style: {
                marginTop: '10px',
                padding: '6px 12px',
                backgroundColor: '#ff0000',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              },
              onClick: () => {
                window.location.href = mailClient.value
              }
            },
            t('buttons.goEmail')
        ) : ''
      ]),
      type: 'success'
    })
  } else {
    ElNotification({
      message: res.errors[0].message,
      type: 'error'
    })
  }

  isLoading.value = false
}
</script>

<style lang="scss" scoped>
.form {
  width: 400px;
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: 15px;

  &__text {
    color: #000000;
    font-weight: 500;
    line-height: 27px;
    letter-spacing: -0.16px;

    & a {
      transition: 0.2s;
      color: #DD6196;
      font-weight: 600;
      line-height: 27px;
      letter-spacing: -0.16px;
      text-decoration: underline;

      @include hover {
        color: #ec247c;
      }
    }
  }

  &__link {
    transition: 0.2s;
    color: #DD6196;
    font-weight: 600;
    line-height: 27px;
    letter-spacing: -0.16px;
    text-decoration: underline;

    @include hover {
      color: #ec247c;
    }
  }

  &__button {
    width: fit-content;
    padding-inline: 20px;
    align-self: flex-end;
  }
}

@media (max-width: 600px) {
  .form {
    width: 100%;
  }
}
</style>